import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Download, FileText } from 'lucide-react';
import { reportDeliveriesApi } from '../../services/api';
import { useDataTransfer } from '../../context/DataTransferContext';
import { formatTimestamp } from '../../utils/dateTime';

/**
 * Landing page for a report subscription's delivery link (/:dbSlug/report-delivery/:key). The email
 * links here rather than to the API because a plain link carries no sign-in token; this page sits
 * inside the signed-in app, under the delivery's own database, then fetches through the api client
 * so the token rides along. The API decides
 * access (recipient or SysAdmin, still holding the report's permission) — this page only shows the
 * answer.
 */

const formatBytes = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

const ReportDeliveryPage = () => {
  const { key, dbSlug } = useParams();
  const navigate = useNavigate();
  const { trackTransfer } = useDataTransfer();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let live = true;
    reportDeliveriesApi.get(key)
      .then(res => {
        if (!live) return;
        // Opened under another database (an edited URL): move to the delivery's own.
        if (res.data.databaseSlug && res.data.databaseSlug.toLowerCase() !== dbSlug?.toLowerCase()) {
          navigate(`/${res.data.databaseSlug}/report-delivery/${key}`, { replace: true });
          return;
        }
        setInfo(res.data);
      })
      .catch(err => { if (live) setError(err.response?.data?.error || 'This report file could not be found.'); });
    return () => { live = false; };
  }, [key, dbSlug, navigate]);

  const download = async () => {
    setDownloading(true);
    try {
      const format = (info.fileName.split('.').pop() || 'file').toLowerCase();
      await trackTransfer({ name: info.reportName, format }, async () => {
        const res = await reportDeliveriesApi.download(key);
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = info.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
    } catch (err) {
      // A blob response carries its JSON error as a Blob, so read it back as text.
      const text = await err.response?.data?.text?.().catch(() => null);
      let message = 'The download failed.';
      try { message = JSON.parse(text).error || message; } catch { /* keep the default */ }
      setError(message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <h1><FileText size={20} />Report Delivery</h1>
        </div>
      </div>

      {error && (
        <div className="stui-module-empty">
          <p>{error}</p>
        </div>
      )}

      {!error && !info && <div className="stui-module-loading">Loading…</div>}

      {!error && info && (
        <div className="stui-module-empty">
          <p>{info.reportName}</p>
          <p className="stui-text-sm-muted">
            {info.fileName} · {formatBytes(info.bytes)}{info.pageCount ? ` · ${info.pageCount.toLocaleString()} pages` : ''}
          </p>
          <p className="stui-text-sm-muted">
            Sent {formatTimestamp(info.createdAt)} · available until {formatTimestamp(info.expiresAt)}
          </p>
          <button className="stui-btn stui-btn-primary" onClick={download} disabled={downloading}>
            <Download size={16} />{downloading ? 'Downloading…' : 'Download'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ReportDeliveryPage;
